/**
 * Report Integrity Footer — Zero Data Loss transparency badge.
 *
 * Shown at the bottom of every Smart Report. Confirms to patients & labs that
 * every parameter from the original laboratory report has been preserved and
 * displayed, or flags an incomplete extraction when it hasn't.
 */
import React from "react";
import { CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";

export default function ReportIntegrityFooter({ smart }) {
  if (!smart) return null;
  const integrity = smart.integrity || {};
  const bios = smart.biomarkers || [];
  const declared = Number(integrity.declared_count ?? smart.declared_total ?? bios.length);
  const extracted = Number(integrity.extracted_count ?? bios.length);
  const status = integrity.status || (declared === extracted ? "validated" : "unknown");
  const confidence = integrity.confidence ?? (declared === extracted ? 1.0 : 0);

  const isOk = status === "validated" || status === "recovered";
  const pct = Math.round(confidence * 100);

  return (
    <div
      data-testid="report-integrity"
      className={`mt-8 rounded-2xl p-5 border-2 ${
        isOk
          ? "bg-emerald-50 border-emerald-200"
          : "bg-amber-50 border-amber-300"
      }`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`w-11 h-11 rounded-full grid place-items-center shrink-0 ${
            isOk ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"
          }`}
        >
          {isOk ? (
            <ShieldCheck className="w-6 h-6" />
          ) : (
            <AlertTriangle className="w-6 h-6" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading font-bold text-ink text-base">
              Report Integrity Check
            </div>
            <span
              data-testid="integrity-status"
              className={`pill text-[10px] uppercase tracking-wider ${
                isOk
                  ? "bg-emerald-500 text-white"
                  : "bg-amber-500 text-white"
              }`}
            >
              {status}
            </span>
          </div>
          <div
            data-testid="integrity-summary"
            className="mt-1.5 font-heading font-semibold text-ink text-[15px]"
          >
            {isOk ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-emerald-600 inline mr-1.5 -mt-0.5" />
                {extracted} of {declared || extracted} parameters successfully extracted and displayed.
              </>
            ) : (
              <>
                {extracted} of {declared} parameters extracted — extraction incomplete.
              </>
            )}
          </div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
            <IntegrityStat label="Parameters extracted" value={extracted} testid="int-extracted" />
            <IntegrityStat label="Parameters declared" value={declared || "—"} testid="int-declared" />
            <IntegrityStat label="Extraction accuracy" value={`${pct}%`} testid="int-accuracy" />
            <IntegrityStat
              label="Source"
              value="Original lab report"
              small
              testid="int-source"
            />
          </div>
          {(integrity.duplicates || []).length > 0 && (
            <div data-testid="integrity-duplicates" className="mt-3 text-[11px] text-amber-700">
              ⚠ Possible duplicates: {integrity.duplicates.slice(0, 5).join(", ")}
            </div>
          )}
          {!isOk && (
            <div className="mt-3 text-[12px] text-ink/70 leading-relaxed">
              We recommend re-uploading a clearer scan of the original report so
              our AI can preserve every laboratory parameter. If the problem
              persists, please contact your lab or EFGH support.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IntegrityStat({ label, value, small = false, testid }) {
  return (
    <div className="rounded-lg bg-white/70 px-3 py-2 border border-white">
      <div className="text-[10px] uppercase tracking-widest text-ink/50 font-heading font-semibold">
        {label}
      </div>
      <div
        data-testid={testid}
        className={`font-heading font-bold text-ink ${
          small ? "text-[12px]" : "text-lg"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
